import React from 'react';
import { useTranslation } from 'react-i18next';

import { FileModel, FileVersionModel } from '../../../../../typings/api/skymap/rest/v1/.common';
import { SkyMapAxiosServiceFactory } from '../../../services/axios/skymap-axios-service-factory';
import { findLastFileVersion } from '../../../utils/models/file-version';
import { publish, SubscriptionTopic } from '../../../viewer/messaging/pubsub';
import { getSharedViewId } from '../../routing/utils';
import { ProjectContext } from '../../state/project-state/project-state';
import { Icon } from '../icon/icon';

interface Props {
  source: { file: FileModel } | { fileVersion: FileVersionModel; fileName: string };
  contentDisposition: 'attachment' | 'inline';
  disabled?: boolean;
}

const DownloadFileIcon = (props: Props) => {
  const { t } = useTranslation();
  const projectContext = React.useContext(ProjectContext);
  const [inProgress, setInProgress] = React.useState(false);
  const sharedViewId = React.useMemo(() => getSharedViewId(), []);

  const resolveFileVersionId = async () => {
    if ('file' in props.source) {
      if (sharedViewId) {
        // todo: This request can be skipped later on when converted to React, and the file itself
        // todo: contains all versions.
        const { data: fileWithVersions } = await SkyMapAxiosServiceFactory.instance
          .createSharedViewServiceV1()
          .getFileById({
            path: {
              sharedViewId,
              fileId: props.source.file.id,
            },
          });
        return findLastFileVersion(fileWithVersions).id;
      }

      // todo: This request can be skipped later on when converted to React, and the file itself
      // todo: contains all versions.
      const { data: fileWithVersions } = await SkyMapAxiosServiceFactory.instance
        .createFileServiceV1()
        .getFileById({
          path: { fileId: props.source.file.id },
        });
      return findLastFileVersion(fileWithVersions).id;
    }

    return props.source.fileVersion.id;
  };

  const download = async () => {
    try {
      setInProgress(true);

      if (props.contentDisposition === 'attachment') {
        await downloadFile();
      } else {
        await openFileInNewTab();
      }
    } catch (err) {
      console.error(err);
      publish(SubscriptionTopic.ToastrMessage, {
        message:
          props.contentDisposition === 'attachment'
            ? t('downloadFile.directDownloadIcon.errorMessage', { ns: 'skyviewAssetMenu' })
            : t('downloadFile.openInNewTabIcon.errorMessage', { ns: 'skyviewAssetMenu' }),
        type: 'error',
      });
    } finally {
      setInProgress(false);
    }
  };

  const resolveFileVersionUrl = async (
    fileVersionId: string,
    contentDisposition: typeof props.contentDisposition,
  ) => {
    if (projectContext?.sharedFolderId) {
      const {
        data: { url },
      } = await SkyMapAxiosServiceFactory.instance.createSharedFolderServiceV1().getFileVersionUrl({
        path: { sharedFolderId: projectContext.sharedFolderId, fileVersionId },
        query: { contentDisposition },
      });
      return url;
    }

    if (sharedViewId) {
      const {
        data: { url },
      } = await SkyMapAxiosServiceFactory.instance.createSharedViewServiceV1().getFileVersionUrl({
        path: { sharedViewId, fileVersionId },
        query: { contentDisposition },
      });
      return url;
    }

    const {
      data: { url },
    } = await SkyMapAxiosServiceFactory.instance.createFileVersionServiceV1().getFileVersionUrl({
      path: { fileVersionId },
      query: { contentDisposition },
    });
    return url;
  };

  const openFileInNewTab = async () => {
    const url = await resolveFileVersionUrl(await resolveFileVersionId(), 'inline');
    window.open(url, '_blank');
  };

  const downloadFile = async () => {
    const url = await resolveFileVersionUrl(await resolveFileVersionId(), 'attachment');
    window.location.href = url;
  };

  return (
    <Icon
      disabled={props.disabled || inProgress}
      icon={[
        'fad',
        inProgress
          ? 'spinner'
          : props.contentDisposition === 'attachment'
            ? 'download'
            : 'arrow-up-right-from-square',
      ]}
      spin={inProgress}
      title={
        props.contentDisposition === 'attachment'
          ? t('downloadFile.directDownloadIcon.title', { ns: 'skyviewAssetMenu' })
          : t('downloadFile.openInNewTabIcon.title', { ns: 'skyviewAssetMenu' })
      }
      onClick={download}
    />
  );
};

export { DownloadFileIcon, Props as DownloadFileIconProps };
